import { useMemo } from 'react';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/toolbar/lib/styles/index.css';
import { PDF_WORKER_URL } from 'shared/lib/pdfWorker';
import { buildHighlightRenderer, hasDrawableHighlight } from './page-highlight';

/**
 * Height of PdfViewer's built-in toolbar (navigation + zoom + download + rotate)
 * including its padding and bottom margin (mb-5 = 20px).
 * Toolbar inner: ~44px content + 16px padding (p-2 top+bottom) + 20px margin = ~80px.
 * Rounded up to 100px for safe spacing so the PDF content area doesn't clip.
 *
 * Used by pages that calculate heightPdf from a measured container:
 *   heightPdf = `${containerHeight - PDF_VIEWER_TOOLBAR_HEIGHT}px`
 */
export const PDF_VIEWER_TOOLBAR_HEIGHT = 100;

// Default toolbar render — unchanged behavior for existing consumers. Callers may
// override the whole toolbar by passing a `renderToolbar(slots)` render prop.
const defaultToolbarRender = (props) => {
  const {
    Download,
    ZoomIn,
    ZoomOut,
    CurrentPageLabel,
    GoToPreviousPage,
    GoToNextPage,
    NumberOfPages,
    Rotate,
  } = props;

  return (
    <div className="flex items-center justify-center mb-5 gap-2 bg-slate-200 p-2">
      <GoToPreviousPage />
      <CurrentPageLabel /> / <NumberOfPages />
      <GoToNextPage />
      <ZoomOut />
      <ZoomIn />
      <Download />
      <Rotate />
    </div>
  );
};

const PdfViewer = ({
  fileUrl,
  heightCard = '600px',
  heightPdf = '500px',
  initialPage,
  renderToolbar,
  wrapperClassName = 'border rounded-md bg-slate-50',
  pdfAreaClassName = 'bg-slate-50',
  highlightPage = null,
  highlightVertices = null,
  highlightRevealToken = null,
}) => {
  const toolbarPluginInstance = toolbarPlugin();
  const { Toolbar } = toolbarPluginInstance;
  const hasInitialPage = Number.isInteger(initialPage) && initialPage >= 0;
  const hasHighlight = hasDrawableHighlight(highlightPage, highlightVertices);

  // Memoized so an unrelated re-render does not hand `Viewer` a fresh
  // `renderPage` identity and re-render every mounted page layer with it.
  // `highlightRevealToken` is in the deps on purpose: re-picking the same finding
  // changes nothing else, and the box has to be told to scroll into view again.
  const renderPage = useMemo(
    () =>
      hasHighlight
        ? buildHighlightRenderer({
            highlightPage,
            highlightVertices,
            revealToken: highlightRevealToken,
          })
        : undefined,
    [hasHighlight, highlightPage, highlightVertices, highlightRevealToken]
  );

  return (
    <>
      <div style={{ height: heightCard }} className={wrapperClassName}>
        <Toolbar>{renderToolbar ?? defaultToolbarRender}</Toolbar>

        <div style={{ height: heightPdf }} className={pdfAreaClassName}>
          <Worker workerUrl={PDF_WORKER_URL}>
            {/* `initialPage` is only read on mount, so a caller that changes it
                to jump pages needs a remount to land there — the same keying
                `PdfViewerOrganism` uses. Callers that pass a static (or no)
                initial page keep a stable key and never remount. */}
            <Viewer
              key={`${fileUrl ?? ''}#${hasInitialPage ? initialPage : 0}`}
              fileUrl={fileUrl}
              plugins={[toolbarPluginInstance]}
              defaultScale={1.2}
              {...(hasInitialPage ? { initialPage } : {})}
              {...(renderPage ? { renderPage } : {})}
            />
          </Worker>
        </div>
      </div>
    </>
  );
};

export default PdfViewer;
